import React from 'react';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { formatRun } from './run';
import { isManifestBased } from './abnormalityHelpers';
import { I18N } from '../../constants';

//
// Dashboard
//

/**
 * Create a breadcrumb for the dashboard screen
 */
export function createDashboardBreadcrumb() {
    return (
        <div>
            <BreadcrumbsItem to="/#/dashboard">Dashboard</BreadcrumbsItem>
        </div>
    );
}

//
// Runs
//

/**
 * Create a breadcrumb for the runs list screen
 */
export function createRunsListBreadcrumb() {
    return (
        <div>
            <BreadcrumbsItem to="/#/runs">Runs</BreadcrumbsItem>
        </div>
    );
}

/**
 * Create a breadcrumb for a run details screen
 *
 * @param run   the run details
 */
export function createRunDetailBreadcrumb(run) {
    return (
        <div>
            <BreadcrumbsItem to="/#/runs">Runs</BreadcrumbsItem>
            <BreadcrumbsItem to={`/#/runs/${run.runId}`}>{formatRun(run)}</BreadcrumbsItem>
        </div>
    );
}

//
// Manifests
//

/**
 * Create a breadcrumb for the manifests list screen
 */
export function createManifestsListBreadcrumb() {
    return (
        <div>
            <BreadcrumbsItem to="/#/manifests">Manifests</BreadcrumbsItem>
        </div>
    );
}

/**
 * Create a breadcrumb for a manifest details screen
 *
 * @param manifest   the manifest details
 */
export function createManifestDetailBreadcrumb(manifest) {
    return (
        <div>
            <BreadcrumbsItem to="/#/manifests">Manifests</BreadcrumbsItem>
            <BreadcrumbsItem to={`/#/manifests/${manifest.manifestId}`}>
                {manifest.manifestNumber}
            </BreadcrumbsItem>
        </div>
    );
}

//
// Abnormalities
//

/**
 * Create a breadcrumb for the abnormality creation screen
 *
 * @param run           the run details
 * @param manifest      the manifest (when manifest based)
 * @param manifestBased whether the abnormality is manifest based
 */
export function createAbnormalityCreationBreadcrumb({
    run,
    manifest,
    manifestBased = false,
    abnormalityType,
}) {
    if (manifestBased) {
        return (
            <div>
                <BreadcrumbsItem to="/#/manifests">Manifests</BreadcrumbsItem>
                <BreadcrumbsItem to={`/#/manifests/${manifest.manifestId}`}>
                    {manifest.manifestNumber}
                </BreadcrumbsItem>
                <BreadcrumbsItem to={`/#/manifests/${manifest.manifestId}/abnormality`}>
                    Create {abnormalityType ? I18N.ABNORMALITIES[abnormalityType] : 'abnormality'}
                </BreadcrumbsItem>
            </div>
        );
    }
    return (
        <div>
            <BreadcrumbsItem to="/#/runs">Runs</BreadcrumbsItem>
            <BreadcrumbsItem to={`/#/runs/${run.runId}`}>{formatRun(run)}</BreadcrumbsItem>
            <BreadcrumbsItem to={`/#/runs/${run.runId}/abnormality`}>
                Create {abnormalityType ? I18N.ABNORMALITIES[abnormalityType] : 'abnormality'}
            </BreadcrumbsItem>
        </div>
    );
}

/**
 * Create a breadcrumb for the abnormality creation screen
 *
 * @param abnormality   the abnormality details
 * @param run           the run on which the abnormality occurred
 * @param manifestBased whether the abnormality is manifest based
 */
export function createAbnormalityDetailBreadcrumb({ run, abnormality, manifestBased = false }) {
    if (manifestBased) {
        const manifest = abnormality.abnormalityManifests[0].manifest;

        return (
            <div>
                <BreadcrumbsItem to="/#/manifests">Manifests</BreadcrumbsItem>
                <BreadcrumbsItem to={`/#/manifests/${manifest.manifestId}`}>
                    {manifest.manifestNumber}
                </BreadcrumbsItem>
                <BreadcrumbsItem
                    to={`/#/manifests/${manifest.manifestId}/abnormality/${
                        abnormality.abnormalityType
                    }/${abnormality.abnormalityId}`}
                >
                    {abnormality.name}
                </BreadcrumbsItem>
            </div>
        );
    }
    return (
        <div>
            <BreadcrumbsItem to="/#/runs">Runs</BreadcrumbsItem>
            <BreadcrumbsItem to={`/#/runs/${run.runId}`}>{formatRun(run)}</BreadcrumbsItem>
            <BreadcrumbsItem
                to={`/#/runs/${run.runId}/abnormality/${abnormality.abnormalityType}/${
                    abnormality.abnormalityId
                }`}
            >
                {abnormality.name}
            </BreadcrumbsItem>
        </div>
    );
}

/**
 * Create a breadcrumb for the abnormality analysis screen
 *
 * @param analysis      the analysis details
 * @param manifestBased whether the abnormality is manifest based
 */
export function createAbnormalityAnalysisBreadcrumb(analysis) {
    const { manifestNavigation: manifest } = analysis;
    const manifestBased = isManifestBased(analysis.genericAbnormalityType);

    if (manifestBased) {
        return (
            <div>
                <BreadcrumbsItem to="/#/manifests">Manifests</BreadcrumbsItem>
                <BreadcrumbsItem to={`/#/manifests/${manifest.manifestId}`}>
                    {manifest.manifestNumber}
                </BreadcrumbsItem>
                <BreadcrumbsItem
                    to={`/#/manifests/${manifest.manifestId}/abnormality/${
                        analysis.abnormalityType
                    }/${analysis.abnormalityId}`}
                >
                    {analysis.name}
                </BreadcrumbsItem>
                <BreadcrumbsItem
                    to={`/#/manifests/${manifest.manifestId}/abnormality/${
                        analysis.abnormalityType
                    }/${analysis.abnormalityId}/recoveryAction/create`}
                >
                    Analysis
                </BreadcrumbsItem>
            </div>
        );
    }
    return (
        <div>
            <BreadcrumbsItem to="/#/runs">Runs</BreadcrumbsItem>
            <BreadcrumbsItem to={`/#/runs/${analysis.runId}`}>
                {formatRun(analysis)}
            </BreadcrumbsItem>
            <BreadcrumbsItem
                to={`/#/runs/${analysis.runId}/abnormality/${analysis.abnormalityType}/${
                    analysis.abnormalityId
                }`}
            >
                {analysis.name}
            </BreadcrumbsItem>
            <BreadcrumbsItem
                to={`/#/runs/${analysis.runId}/abnormality/${analysis.abnormalityType}/${
                    analysis.abnormalityId
                }/recoveryAction/create`}
            >
                Analysis
            </BreadcrumbsItem>
        </div>
    );
}

//
// Recovery actions
//

/**
 * Create a breadcrumb for a recovery action details screen
 *
 * @param recoveryAction    the recoveryAction details
 * @param abnormalityType   the type of abnormality
 */
export function createRecoveryActionDetailBreadcrumb(recoveryAction) {
    const {
        abnormalityRun: run,
        abnormality,
        abnormality: { manifestNavigation: manifest },
    } = recoveryAction;
    const manifestBased = isManifestBased(abnormality.genericAbnormalityType);

    if (manifestBased) {
        return (
            <div>
                <BreadcrumbsItem to="/#/manifests">Manifests</BreadcrumbsItem>
                <BreadcrumbsItem to={`/#/manifests/${manifest.manifestId}`}>
                    {manifest.manifestNumber}
                </BreadcrumbsItem>
                <BreadcrumbsItem
                    to={`/#/manifests/${manifest.manifestId}/abnormality/${
                        abnormality.abnormalityType
                    }/${abnormality.abnormalityId}`}
                >
                    {abnormality.abnormalityName}
                </BreadcrumbsItem>
                <BreadcrumbsItem
                    to={`/#/manifests/${manifest.manifestId}/abnormality/${
                        abnormality.abnormalityType
                    }/${abnormality.abnormalityId}/recoveryAction/${
                        recoveryAction.recoveryActionType
                    }/${recoveryAction.recoveryActionId}`}
                >
                    {recoveryAction.name}
                </BreadcrumbsItem>
            </div>
        );
    }
    return (
        <div>
            <BreadcrumbsItem to="/#/runs">Runs</BreadcrumbsItem>
            <BreadcrumbsItem to={`/#/runs/${run.runId}`}>{formatRun(run)}</BreadcrumbsItem>
            <BreadcrumbsItem
                to={`/#/runs/${run.runId}/abnormality/${abnormality.abnormalityType}/${
                    abnormality.abnormalityId
                }`}
            >
                {abnormality.abnormalityName}
            </BreadcrumbsItem>
            <BreadcrumbsItem
                to={`/#/runs/${run.runId}/abnormality/${abnormality.abnormalityType}/${
                    abnormality.abnormalityId
                }/recoveryAction/${recoveryAction.recoveryActionType}/${
                    recoveryAction.recoveryActionId
                }`}
            >
                {recoveryAction.name}
            </BreadcrumbsItem>
        </div>
    );
}

/**
 * Create a breadcrumb for a generic abnormality journey builder screen
 *
 * @param abnormalityManifest   the abnormality manifest
 */
export function createJourneyBuilderBreadcrumb(
    abnormalityAnalysis,
    abnormalityManifest,
    manifestBased
) {
    const { abnormality, manifest } = abnormalityManifest;

    if (manifestBased) {
        return (
            <div>
                <BreadcrumbsItem to="/#/manifests">Manifests</BreadcrumbsItem>
                <BreadcrumbsItem to={`/#/manifests/${manifest.manifestId}`}>
                    {manifest.manifestNumber}
                </BreadcrumbsItem>
                <BreadcrumbsItem
                    to={`/#/manifests/${manifest.manifestId}/abnormality/${
                        abnormality.abnormalityType
                    }/${abnormality.abnormalityId}`}
                >
                    {abnormality.name}
                </BreadcrumbsItem>
                <BreadcrumbsItem
                    to={`/#/manifests/${manifest.manifestId}/abnormality/${
                        abnormality.abnormalityType
                    }/${abnormality.abnormalityId}/genericAbnormalityAnalysis`}
                >
                    Analysis
                </BreadcrumbsItem>
                <BreadcrumbsItem
                    to={`/#/manifests/${manifest.manifestId}/abnormality/${
                        abnormality.abnormalityType
                    }/${abnormality.abnormalityId}/genericAbnormalityAnalysis/journeyBuilder`}
                >
                    Journey builder
                </BreadcrumbsItem>
            </div>
        );
    }
    return (
        <div>
            <BreadcrumbsItem to="/#/runs">Runs</BreadcrumbsItem>
            <BreadcrumbsItem to={`/#/runs/${abnormality.runId}`}>
                {formatRun(abnormalityAnalysis)}
            </BreadcrumbsItem>
            <BreadcrumbsItem
                to={`/#/runs/${abnormality.runId}/abnormality/${abnormality.abnormalityType}/${
                    abnormality.abnormalityId
                }`}
            >
                {abnormality.name}
            </BreadcrumbsItem>
            <BreadcrumbsItem
                to={`/#/runs/${abnormality.runId}/abnormality/${abnormality.abnormalityType}/${
                    abnormality.abnormalityId
                }/genericAbnormalityAnalysis`}
            >
                Analysis
            </BreadcrumbsItem>
            <BreadcrumbsItem
                to={`/#/runs/${abnormality.runId}/abnormality/${abnormality.abnormalityType}/${
                    abnormality.abnormalityId
                }/genericAbnormalityAnalysis/journeyBuilder`}
            >
                Journey builder
            </BreadcrumbsItem>
        </div>
    );
}

//
// Reports
//

/**
 * Create a breadcrumb for the RFU screen
 */
export function createRFUBreadcrumb() {
    return (
        <div>
            <BreadcrumbsItem to="/#/reports/rfu">Route Follow Up</BreadcrumbsItem>
        </div>
    );
}

/**
 * Create a breadcrumb for the abnormality KPI screen
 */
export function createAbnormalityKPIBreadcrumb() {
    return (
        <div>
            <BreadcrumbsItem to="/#/reports/kpi">Abnormality KPI</BreadcrumbsItem>
        </div>
    );
}

/**
 * Create a breadcrumb for the planning KPI screen
 */
export function createPlanningKPIBreadcrumb() {
    return (
        <div>
            <BreadcrumbsItem to="/#/reports/planningKpi">Planning KPI</BreadcrumbsItem>
        </div>
    );
}


export function createIlocsDataDownloadBreadcrumb() {
    return (
        <div>
            <BreadcrumbsItem to="/#/reports/ilocsDataDownload">Ilocs Data Download</BreadcrumbsItem>
        </div>
    );
}
